import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Link } from 'react-router-dom';
import { ArrowRight, Shield, Cpu, Target, Layers } from 'lucide-react';
import { SEO } from '../components/SEO';

export function AboutPage() {
  const { currentLanguage } = useLanguage();

  const content = {
    en: {
      hero: {
        eyebrow: 'About',
        title: 'Engineering Systems That Endure',
        subtitle: 'We design AI, financial and enterprise platforms built for reliability, scale, and long-term operational stability.'
      },
      philosophy: {
        title: 'What We Believe',
        items: [
          'Software is infrastructure.',
          'Reliability is non-negotiable.',
          'Architecture matters more than speed.',
          'We build for years, not sprints.'
        ]
      },
      founder: {
        text: 'Founded by engineers with deep experience in financial technology and AI-driven systems, Datex was built on a simple principle:',
        highlight: 'software must endure pressure.'
      },
      focus: {
        title: 'Our Focus',
        subtitle: 'We specialize in high-performance, data-intensive systems where reliability and architecture matter.',
        areas: [
          {
            icon: Cpu,
            title: 'AI & Intelligent Systems',
            desc: 'Predictive models, decision engines, and LLM-powered contextual systems.'
          },
          {
            icon: Layers,
            title: 'Financial Infrastructure',
            desc: 'Trading systems, risk modeling, and mission-critical financial logic.'
          },
          {
            icon: Shield,
            title: 'Enterprise Platforms',
            desc: 'Scalable SaaS, CRM systems, and data-intensive business applications.'
          },
          {
            icon: Target,
            title: 'Production-Grade MVPs',
            desc: 'From concept validation to market-ready products built to scale.'
          }
        ]
      },
      metrics: {
        title: 'Track Record',
        items: [
          { value: '7+', label: 'Years', desc: 'Building mission-critical systems' },
          { value: '50+', label: 'Projects', desc: 'Financial & AI deployments' },
          { value: '99.9%', label: 'Uptime', desc: 'Production-grade infrastructure' }
        ]
      },
      company: {
        label: 'Company',
        name: 'Datex Bilişim AŞ',
        location: 'İzmir, Turkey',
        founded: 'Est. 2018'
      },
      cta: {
        title: 'Ready to Build?',
        text: 'Let\'s discuss how we can engineer a solution for your business.',
        button: 'Start a Conversation'
      }
    },
    tr: {
      hero: {
        eyebrow: 'Hakkımızda',
        title: 'Dayanıklı Sistemler Mühendisliği',
        subtitle: 'Güvenilirlik, ölçeklenebilirlik ve uzun vadeli operasyonel stabilite için tasarlanmış yapay zeka, finansal ve kurumsal platformlar geliştiriyoruz.'
      },
      philosophy: {
        title: 'İnandıklarımız',
        items: [
          'Yazılım bir altyapıdır.',
          'Güvenilirlik pazarlık konusu değildir.',
          'Mimari, hızdan daha önemlidir.',
          'Sprintler için değil, yıllar için inşa ediyoruz.'
        ]
      },
      founder: {
        text: 'Finansal teknoloji ve yapay zeka sistemlerinde derin deneyime sahip mühendisler tarafından kurulan Datex, basit bir prensip üzerine inşa edildi:',
        highlight: 'yazılım baskıya dayanmalıdır.'
      },
      focus: {
        title: 'Odak Alanlarımız',
        subtitle: 'Güvenilirlik ve mimarinin önemli olduğu yüksek performanslı, veri yoğun sistemlerde uzmanlaşıyoruz.',
        areas: [
          {
            icon: Cpu,
            title: 'Yapay Zeka & Akıllı Sistemler',
            desc: 'Tahminleme modelleri, karar motorları ve LLM destekli bağlamsal sistemler.'
          },
          {
            icon: Layers,
            title: 'Finansal Altyapı',
            desc: 'Trading sistemleri, risk modellemesi ve kritik öneme sahip finansal mantık.'
          },
          {
            icon: Shield,
            title: 'Kurumsal Platformlar',
            desc: 'Ölçeklenebilir SaaS, CRM sistemleri ve veri yoğun iş uygulamaları.'
          },
          {
            icon: Target,
            title: 'Production Seviye MVP\'ler',
            desc: 'Konsept doğrulamadan ölçeklenmeye hazır ürünlere.'
          }
        ]
      },
      metrics: {
        title: 'Geçmiş Performans',
        items: [
          { value: '7+', label: 'Yıl', desc: 'Kritik sistemler geliştirme' },
          { value: '50+', label: 'Proje', desc: 'Finansal & AI deployments' },
          { value: '99.9%', label: 'Uptime', desc: 'Production seviye altyapı' }
        ]
      },
      company: {
        label: 'Şirket',
        name: 'Datex Bilişim AŞ',
        location: 'İzmir, Türkiye',
        founded: 'Kuruluş 2018'
      },
      cta: {
        title: 'İnşa Etmeye Hazır mısınız?',
        text: 'İşletmeniz için nasıl bir çözüm mühendisliği yapabileceğimizi konuşalım.',
        button: 'Bir Görüşme Başlat'
      }
    },
    de: {
      hero: {
        eyebrow: 'Über uns',
        title: 'Engineering Systems That Endure',
        subtitle: 'Wir entwickeln KI-, Finanz- und Unternehmensplattformen für Zuverlässigkeit, Skalierbarkeit und langfristige Betriebsstabilität.'
      },
      philosophy: {
        title: 'Woran Wir Glauben',
        items: [
          'Software ist Infrastruktur.',
          'Zuverlässigkeit ist nicht verhandelbar.',
          'Architektur ist wichtiger als Geschwindigkeit.',
          'Wir bauen für Jahre, nicht für Sprints.'
        ]
      },
      founder: {
        text: 'Gegründet von Ingenieuren mit tiefgreifender Erfahrung in Finanztechnologie und KI-gesteuerten Systemen, wurde Datex auf einem einfachen Prinzip aufgebaut:',
        highlight: 'Software muss Druck standhalten.'
      },
      focus: {
        title: 'Unser Fokus',
        subtitle: 'Wir spezialisieren uns auf leistungsstarke, datenintensive Systeme, bei denen Zuverlässigkeit und Architektur wichtig sind.',
        areas: [
          {
            icon: Cpu,
            title: 'KI & Intelligente Systeme',
            desc: 'Prädiktive Modelle, Entscheidungsmotoren und LLM-gestützte kontextuelle Systeme.'
          },
          {
            icon: Layers,
            title: 'Finanzinfrastruktur',
            desc: 'Handelssysteme, Risikomodellierung und unternehmenskritische Finanzlogik.'
          },
          {
            icon: Shield,
            title: 'Enterprise-Plattformen',
            desc: 'Skalierbare SaaS, CRM-Systeme und datenintensive Geschäftsanwendungen.'
          },
          {
            icon: Target,
            title: 'Production-Grade MVPs',
            desc: 'Von der Konzeptvalidierung bis zu marktfähigen Produkten.'
          }
        ]
      },
      metrics: {
        title: 'Erfolgsbilanz',
        items: [
          { value: '7+', label: 'Jahre', desc: 'Entwicklung kritischer Systeme' },
          { value: '50+', label: 'Projekte', desc: 'Finanz- & KI-Deployments' },
          { value: '99.9%', label: 'Uptime', desc: 'Production-grade Infrastruktur' }
        ]
      },
      company: {
        label: 'Unternehmen',
        name: 'Datex Bilişim AŞ',
        location: 'Izmir, Türkei',
        founded: 'Gegr. 2018'
      },
      cta: {
        title: 'Bereit zu Bauen?',
        text: 'Lassen Sie uns besprechen, wie wir eine Lösung für Ihr Unternehmen entwickeln können.',
        button: 'Gespräch Starten'
      }
    }
  };

  const t = content[currentLanguage] || content.en;

  return (
    <>
      <SEO 
        title={currentLanguage === 'tr' ? 'Hakkımızda' : currentLanguage === 'de' ? 'Über uns' : 'About'}
        description={t.hero.subtitle}
      />
      
      <div className="min-h-screen">
        {/* Dark Hero Section */}
        <section 
          className="pt-32 pb-12 md:pt-36 md:pb-14 relative"
          style={{ 
            background: 'linear-gradient(180deg, #121821 0%, #0E1319 100%)',
            minHeight: '420px'
          }}
        >
          {/* Subtle grid pattern */}
          <div 
            className="absolute inset-0 opacity-[0.03]"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
            }}
          />
          
          <div className="relative z-10 max-w-4xl mx-auto px-4 md:px-8">
            <span 
              className="text-xs uppercase tracking-widest mb-4 block"
              style={{ color: '#14B8A6', letterSpacing: '0.15em' }}
            >
              {t.hero.eyebrow}
            </span>
            <h1 
              className="text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight mb-6"
              style={{ color: '#FFFFFF' }}
            >
              {t.hero.title}
            </h1>
            <p 
              className="text-lg md:text-xl leading-relaxed max-w-2xl mb-6"
              style={{ color: 'rgba(255, 255, 255, 0.7)' }}
            >
              {t.hero.subtitle}
            </p>
            {/* Curated note */}
            <p 
              className="text-xs uppercase tracking-wider"
              style={{ 
                color: 'rgba(255, 255, 255, 0.4)', 
                fontFamily: 'JetBrains Mono, monospace',
                letterSpacing: '0.1em'
              }}
            >
              {currentLanguage === 'tr' ? 'Mühendislik otoritesi. 2018\'den beri.' : 
               currentLanguage === 'de' ? 'Engineering-Autorität. Seit 2018.' :
               'Engineering authority. Since 2018.'}
            </p>
          </div>
        </section>

        {/* Light Content Section */}
        <div className="bg-white pb-20">
        <div className="max-w-4xl mx-auto px-4 md:px-8 pt-16 md:pt-20">

          {/* Philosophy Section - Engineering Manifesto */}
          <div 
            className="mb-20 p-8 md:p-10 rounded-2xl"
            style={{ 
              background: 'linear-gradient(180deg, rgba(15, 118, 110, 0.08) 0%, rgba(15, 118, 110, 0.02) 100%)',
              border: '1px solid rgba(15, 118, 110, 0.15)'
            }}
          >
            <h2 
              className="text-xs uppercase tracking-widest mb-8"
              style={{ color: '#0F766E' }}
            >
              {t.philosophy.title}
            </h2>
            <div className="space-y-4">
              {t.philosophy.items.map((item, idx) => (
                <div 
                  key={idx}
                  className="flex items-center gap-4"
                >
                  <span 
                    className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                    style={{ background: '#0F766E' }}
                  />
                  <span 
                    className="text-lg md:text-xl font-medium"
                    style={{ color: 'var(--text-on-light-primary)' }}
                  >
                    {item}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Founder Note */}
          <div className="mb-20">
            <blockquote 
              className="text-xl md:text-2xl leading-relaxed"
              style={{ color: 'var(--text-on-light-secondary)' }}
            >
              "{t.founder.text}{' '}
              <span style={{ color: '#0F766E' }}>{t.founder.highlight}</span>"
            </blockquote>
          </div>

          {/* Metrics - Track Record */}
          <div className="mb-20">
            <h2 
              className="text-xs uppercase tracking-widest mb-8"
              style={{ color: '#0F766E' }}
            >
              {t.metrics.title}
            </h2>
            <div 
              className="grid grid-cols-3 gap-6 p-6 rounded-xl"
              style={{ 
                background: '#F8FAFB',
                border: '1px solid rgba(15, 118, 110, 0.1)'
              }}
            >
              {t.metrics.items.map((item, idx) => (
                <div key={idx} className="text-center">
                  <div 
                    className="text-3xl md:text-4xl font-bold mb-1"
                    style={{ color: '#0F766E' }}
                  >
                    {item.value}
                  </div>
                  <div 
                    className="text-sm font-medium mb-1"
                    style={{ color: 'var(--text-on-light-primary)' }}
                  >
                    {item.label}
                  </div>
                  <div 
                    className="text-xs"
                    style={{ color: 'var(--text-on-light-muted)' }}
                  >
                    {item.desc}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Focus Areas */}
          <div className="mb-20">
            <h2 
              className="text-xs uppercase tracking-widest mb-4"
              style={{ color: '#0F766E' }}
            >
              {t.focus.title}
            </h2>
            <p 
              className="text-base md:text-lg mb-10 max-w-2xl"
              style={{ color: 'var(--text-on-light-muted)' }}
            >
              {t.focus.subtitle}
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              {t.focus.areas.map((area, idx) => {
                const IconComponent = area.icon;
                return (
                  <div 
                    key={idx}
                    className="p-6 rounded-xl"
                    style={{ 
                      background: 'linear-gradient(180deg, rgba(15, 118, 110, 0.05) 0%, rgba(15, 118, 110, 0.01) 100%)',
                      border: '1px solid rgba(15, 118, 110, 0.1)'
                    }}
                  >
                    <div className="flex items-start gap-4">
                      <div 
                        className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                        style={{ 
                          background: 'rgba(15, 118, 110, 0.1)',
                          border: '1px solid rgba(15, 118, 110, 0.15)'
                        }}
                      >
                        <IconComponent 
                          className="w-5 h-5" 
                          style={{ color: '#0F766E' }}
                          strokeWidth={1.5}
                        />
                      </div>
                      <div>
                        <h3 
                          className="text-base font-semibold mb-1"
                          style={{ color: 'var(--text-on-light-primary)' }}
                        >
                          {area.title}
                        </h3>
                        <p 
                          className="text-sm"
                          style={{ color: 'var(--text-on-light-muted)' }}
                        >
                          {area.desc}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Company Info */}
          <div className="mb-20">
            <h2 
              className="text-xs uppercase tracking-widest mb-6"
              style={{ color: '#0F766E' }}
            >
              {t.company.label}
            </h2>
            <div 
              className="flex flex-wrap gap-6 text-sm"
              style={{ color: 'var(--text-on-light-muted)' }}
            >
              <span className="font-medium" style={{ color: 'var(--text-on-light-secondary)' }}>
                {t.company.name}
              </span>
              <span>·</span>
              <span>{t.company.location}</span>
              <span>·</span>
              <span>{t.company.founded}</span>
            </div>
          </div>

          {/* CTA */}
          <div 
            className="p-8 md:p-10 rounded-2xl text-center"
            style={{ 
              background: 'linear-gradient(180deg, rgba(15, 118, 110, 0.08) 0%, rgba(15, 118, 110, 0.02) 100%)',
              border: '1px solid rgba(15, 118, 110, 0.15)'
            }}
          >
            <h2 
              className="text-2xl md:text-3xl font-semibold mb-3"
              style={{ color: 'var(--text-on-light-primary)' }}
            >
              {t.cta.title}
            </h2>
            <p 
              className="text-base mb-6"
              style={{ color: 'var(--text-on-light-muted)' }}
            >
              {t.cta.text}
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-lg font-medium text-sm transition-all duration-300 hover:gap-3 hover:opacity-90"
              style={{ 
                background: 'linear-gradient(135deg, #0F766E 0%, #14B8A6 100%)',
                color: '#FFFFFF',
                border: '1px solid rgba(15, 118, 110, 0.3)'
              }}
            >
              {t.cta.button}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

        </div>
        </div>
      </div>
    </>
  );
}
